import { FC, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Clock, Users, Calendar, ArrowRight } from 'lucide-react';
import { Card, CardBody } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Badge } from '../../components/ui/Badge';
import { cn } from '../../utils/cn';
import { formatCurrencyAmount } from '../../utils/currency';
import type { Service, ServiceCategory } from '../../types/service.types';
import { ServiceDetailModal } from './ServiceDetailModal';

interface ServiceCardProps {
  service: Service;
  layout?: 'vertical' | 'horizontal';
}

/**
 * Maps service categories to badge variants
 */
const categoryBadgeVariant: Record<ServiceCategory, 'primary' | 'secondary' | 'success' | 'info'> =
  {
    Relaxation: 'primary',
    Therapeutic: 'secondary',
    Sports: 'success',
    Specialized: 'info',
  };

/**
 * Maps service categories to gradient colors for placeholder images
 */
const categoryGradient: Record<ServiceCategory, string> = {
  Relaxation: 'from-teal-400 to-cyan-500',
  Therapeutic: 'from-violet-400 to-purple-500',
  Sports: 'from-green-400 to-emerald-500',
  Specialized: 'from-blue-400 to-indigo-500',
};

/**
 * Formats duration in minutes to a readable string
 */
function formatDuration(minutes: number): string {
  if (minutes < 60) {
    return `${minutes} min`;
  }
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  if (remainingMinutes === 0) {
    return `${hours} hr`;
  }
  return `${hours} hr ${remainingMinutes} min`;
}


export const ServiceCard: FC<ServiceCardProps> = ({ service, layout = 'vertical' }) => {
  const navigate = useNavigate();
  const [isModalOpen, setIsModalOpen] = useState(false);

  const handleBookNow = () => {
    navigate(`/booking?service=${service.slug}`);
  };

  const handleOpenDetails = () => {
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  const isHorizontal = layout === 'horizontal';

  return (
    <>
      <Card
        data-testid="service-card"
        className={cn(
          'overflow-hidden transition-shadow duration-300 hover:shadow-xl',
          isHorizontal && 'flex flex-row'
        )}
        padding="none"
      >
        {/* Image Section */}
        <div
          className={cn(
            'relative overflow-hidden',
            isHorizontal ? 'w-48 flex-shrink-0 aspect-square' : 'aspect-[4/3]'
          )}
        >
          {service.imageUrl ? (
            <img
              src={service.imageUrl}
              alt={service.name}
              className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105 hover:scale-105"
            />
          ) : (
            <div
              className={cn(
                'h-full w-full bg-gradient-to-br transition-transform duration-300 hover:scale-105',
                categoryGradient[service.category]
              )}
            >
              {/* Decorative pattern overlay */}
              <div className="absolute inset-0 opacity-20">
                <svg
                  className="h-full w-full"
                  viewBox="0 0 100 100"
                  preserveAspectRatio="none"
                  aria-hidden="true"
                >
                  <pattern
                    id={`pattern-${service.id}`}
                    x="0"
                    y="0"
                    width="20"
                    height="20"
                    patternUnits="userSpaceOnUse"
                  >
                    <circle cx="10" cy="10" r="2" fill="currentColor" />
                  </pattern>
                  <rect x="0" y="0" width="100" height="100" fill={`url(#pattern-${service.id})`} />
                </svg>
              </div>
            </div>
          )}

          {/* Category Badge */}
          <div className="absolute left-3 top-3">
            <Badge
              variant={categoryBadgeVariant[service.category]}
              size="sm"
              className="bg-white/90 backdrop-blur-sm"
            >
              {service.category}
            </Badge>
          </div>
        </div>

        {/* Content Section */}
        <CardBody className={cn('flex flex-col', isHorizontal ? 'flex-1 p-4' : 'p-4')}>
          {/* Header */}
          <div className="mb-2">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-slate-100 line-clamp-1">{service.name}</h3>
          </div>

          {/* Meta Info */}
          <div
            className={cn(
              'flex flex-wrap gap-3 text-sm text-gray-600 dark:text-slate-400',
              isHorizontal ? 'mb-2' : 'mb-3'
            )}
          >
            <span className="flex items-center gap-1">
              <Clock className="h-4 w-4" aria-hidden="true" />
              <span>{formatDuration(service.duration)}</span>
            </span>

            {service.availableTherapistCount !== undefined && (
              <span className="flex items-center gap-1">
                <Users className="h-4 w-4" aria-hidden="true" />
                <span>
                  {service.availableTherapistCount}{' '}
                  {service.availableTherapistCount === 1 ? 'therapist' : 'therapists'}
                </span>
              </span>
            )}
          </div>

          {/* Price */}
          <div className="mb-3">
            <span className="text-xl font-bold text-primary-600">
              {formatCurrencyAmount(service.basePrice)}
            </span>
          </div>

          {/* Description */}
          <p
            className={cn(
              'text-sm text-gray-600 dark:text-slate-400 flex-grow',
              isHorizontal ? 'line-clamp-2' : 'line-clamp-3'
            )}
          >
            {service.description}
          </p>

          {/* Actions */}
          <div
            className={cn('flex gap-2 mt-4', isHorizontal ? 'flex-row' : 'flex-col sm:flex-row')}
          >
            <Button
              variant="outline"
              size="sm"
              onClick={handleOpenDetails}
              className="flex-1"
              aria-label={`View details for ${service.name}`}
            >
              Details
            </Button>
            <Button
              variant="primary"
              size="sm"
              onClick={handleBookNow}
              rightIcon={<ArrowRight className="h-4 w-4" />}
              className="flex-1"
              aria-label={`Book ${service.name}`}
            >
              <Calendar className="h-4 w-4 mr-1" aria-hidden="true" />
              Book Now
            </Button>
          </div>
        </CardBody>
      </Card>

      {/* Detail Modal */}
      <ServiceDetailModal service={service} isOpen={isModalOpen} onClose={handleCloseModal} />
    </>
  );
};
